import type { Cart, CheckoutCart, DeliveryMethod } from "@dransay/api/models";
import { buildCheckoutCartFromLiveCart } from "./cross-sell/cross-sell.cart-sync";

function deliverySnapshot(methods: readonly DeliveryMethod[] = []) {
	return methods
		.map((method) => [
			method.uuid,
			method.enabled,
			method.kind,
			method.description,
			method.cost,
			method.freeFrom,
			method.provider,
			method.minDays,
			method.maxDays,
			method.orderBefore,
			method.type,
		])
		.sort(([left], [right]) => String(left).localeCompare(String(right)));
}

function commercialSnapshot(cart: Cart | CheckoutCart) {
	return JSON.stringify([
		cart.totalPrice,
		cart.items
			.map((item) => [item.id, item.product?.price, item.product?.packageSize])
			.sort(([left], [right]) => String(left).localeCompare(String(right))),
		deliverySnapshot(cart.vendor?.deliveryMethods),
		(cart.fulfillmentGroups ?? [])
			.map((group) => [
				group.id,
				group.itemsPrice,
				deliverySnapshot(group.vendor.deliveryMethods),
			])
			.sort(([left], [right]) => String(left).localeCompare(String(right))),
	]);
}

/** Refresh server-owned commercial data without resetting questionnaire or payment progress. */
export function refreshCheckoutCart(
	stored: CheckoutCart,
	current: Cart,
): CheckoutCart {
	if (commercialSnapshot(stored) === commercialSnapshot(current)) return stored;

	const refreshed = buildCheckoutCartFromLiveCart({
		checkoutCart: stored,
		items: current.items,
		totalPrice: current.totalPrice,
		vendor: current.vendor,
		deliveryMethodUuid: stored.deliveryMethodUuid,
		fulfillmentGroups: current.fulfillmentGroups,
	});

	refreshed.fulfillmentGroups = refreshed.fulfillmentGroups.map((group) => {
		const previous = stored.fulfillmentGroups?.find(
			(entry) => entry.id === group.id,
		);
		const selected = group.vendor.deliveryMethods.find(
			(method) =>
				method.uuid === previous?.deliveryMethodUuid && method.enabled,
		);
		if (!selected || selected.uuid === group.deliveryMethodUuid) return group;
		// A local choice has no authoritative quote in this response. Keep the choice,
		// but require confirmation before showing shipping or allowing payment.
		return {
			...group,
			deliveryMethodUuid: selected.uuid,
			deliverySelectionStale: true,
			shippingPrice: null,
		};
	});
	return refreshed;
}
